import { $enum } from "ts-enum-util";

import type { ConsumptionGranularity } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { DATE_FORMAT } from "~/constants/date.constant";

import { GRANULARITIES, PERIOD_PRESETS } from "../consumption.constant";
import { Comparison, PeriodPreset, type ChartView, type ComparedBuilding } from "../types/consumption.type";
import { appliedOptions, comparisonGranularities } from "./comparison.helper";
import type { ConsumptionSearch } from "./consumption-search.helper";
import { finerGranularities, formatBucket, spanRange, unavailableReason } from "./period.helper";

type ViewChange = { search: Partial<ConsumptionSearch>; reason?: never } | { search?: never; reason: string };

const drillDown = (
  view: ChartView,
  start: string,
  building: ComparedBuilding,
  today: string,
  compared = false,
): ViewChange | undefined => {
  const { granularity } = view.period;
  const finer = finerGranularities(granularity);
  const next = finer.at(-1);
  if (!next) return undefined;
  const shift = compared ? view.comparison?.shift : undefined;
  const range = shift && view.comparison ? view.comparison.range : view.period.range;
  const bucketFrom = formatBucket(start, DATE_FORMAT, building.timezone, shift);
  const bucketTo = dayjs(bucketFrom).endOf(GRANULARITIES[granularity].unit).format(DATE_FORMAT);
  const bucketRange = {
    from: bucketFrom < range.from ? range.from : bucketFrom,
    to: bucketTo > range.to ? range.to : bucketTo,
  };
  const granularities = comparisonGranularities(bucketRange, appliedOptions(view), building, today);
  const target = granularities.filter(candidate => finer.includes(candidate)).at(-1);
  if (!target) return { reason: unavailableReason(bucketRange, next, granularities, today) };
  return { search: { range: bucketRange, granularity: target, preset: undefined } };
};

const granularityChoice = (
  view: ChartView,
  granularity: ConsumptionGranularity,
  building: ComparedBuilding,
  today: string,
): Partial<ConsumptionSearch> => {
  if (view.granularities.includes(granularity)) return { granularity };
  const preset = $enum(PeriodPreset)
    .getValues()
    .find(candidate => PERIOD_PRESETS[candidate].granularity === granularity);
  const range = spanRange(preset ? PERIOD_PRESETS[preset].span : GRANULARITIES[granularity].maxRange, today);
  const keepsComparison = comparisonGranularities(range, appliedOptions(view), building, today).includes(granularity);
  return {
    preset,
    range: preset ? undefined : range,
    granularity,
    ...(!keepsComparison && { compare: Comparison.NONE, degreeDays: false, objective: undefined }),
  };
};

export { drillDown, granularityChoice };
